import { CloseOutlined, EmailOutlined, Facebook, Instagram, Language, LinkedIn, PersonOutlineOutlined, Phone, X } from '@mui/icons-material';
import { Divider } from '@mui/material';
import { useScrollLock } from 'hooks/useScrollLock';
import usePropertyRequestsStore from 'store/propertyRequestsStore';
import { urgencyOptions } from 'util/urgencySelector';

interface Props {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function SavedLeadDetailsModal({ isOpen, setIsOpen }: Props) {
    const { lockScroll, unlockScroll } = useScrollLock();
    const { selectedLead } = usePropertyRequestsStore();

    if (isOpen) {
        lockScroll();
    } else {
        unlockScroll();
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black/50 z-50 font-poppins ">
            <div
                className={`fixed right-0 top-0 h-full w-full max-w-5xl overflow-auto bg-white/100 z-50 motion-safe:animate-[slideLeft_400ms_ease-out_forwards]`}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-3 border-b sticky top-0 bg-white z-10">
                    <h2 className="text-lg text-my-black/80 font-semibold">Lead Details</h2>
                    <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <CloseOutlined className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Property Type */}
                    <div className="flex items-start gap-4">
                        <div className="p-3 bg-blue-50 rounded-lg">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                                />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-my-black/80 ">{selectedLead.title}</h3>
                            <p className="text-my-black/60">
                                {selectedLead.definedArea ? selectedLead.definedArea : selectedLead.cities.map((city) => city.city).join(', ')} •{' '}
                                {selectedLead.propertyType}
                            </p>
                        </div>
                    </div>
                    <Divider />

                    {/* Description */}
                    <div>
                        <h4 className="font-medium mb-2 text-my-black/60 ">Request Description</h4>
                        <p className="text-my-black/90 font-normal text-base">{selectedLead.description}</p>
                    </div>

                    {/* Features */}
                    <div>
                        <h4 className="font-medium mb-4 text-my-black/60 ">Request Features</h4>
                        <div className="grid grid-cols-1 default-md:grid-cols-2 default-lg:grid-cols-3 gap-6 mb-8">
                            {selectedLead.definedArea ? (
                                <Details title="Area" value={selectedLead.definedArea} />
                            ) : (
                                <>
                                    <Details title="Country" value={selectedLead.propertyCountry} />
                                    <Details title="State" value={selectedLead.states?.map((state) => state.state).join(', ')} />
                                    {selectedLead.propertyNeighborhood && selectedLead.propertyNeighborhood.length > 0 && (
                                        <Details title="Neighborhood" value={selectedLead.propertyNeighborhood.join(', ')} />
                                    )}
                                </>
                            )}
                            {selectedLead.minBudget >= 0 && selectedLead.maxBudget > 0 && (
                                <Details title="Budget" value={`€${selectedLead.minBudget} - €${selectedLead.maxBudget}`} />
                            )}
                            {selectedLead.minConstructionArea >= 0 && selectedLead.maxConstructionArea > 0 && (
                                <Details title="Constructed area" value={`${selectedLead.minConstructionArea} - ${selectedLead.maxConstructionArea} m²`} />
                            )}
                            {selectedLead.minPlotArea >= 0 && selectedLead.maxPlotArea > 0 && (
                                <Details title="Plot area" value={`${selectedLead.minPlotArea} - ${selectedLead.maxPlotArea} m²`} />
                            )}
                            {selectedLead.minBedrooms && selectedLead.maxBedrooms && (
                                <Details title="Approx. Bedrooms" value={`${selectedLead.minBedrooms} - ${selectedLead.maxBedrooms}`} />
                            )}
                            {selectedLead.minBathrooms && selectedLead.maxBathrooms && (
                                <Details title="Approx. Bathrooms" value={`${selectedLead.minBathrooms} - ${selectedLead.maxBathrooms}`} />
                            )}
                            <Details title="Urgency" value={urgencyOptions.find((option) => option.value === selectedLead.urgency)?.label!} />
                            {selectedLead.furnishing && <Details title="Furnishing" value={`${selectedLead.furnishing}`} />}
                            <Details title="Agent Commission" value={`${selectedLead.commission}%`} />
                            {selectedLead.buyerProfile && <Details title="Buyer Profile" value={selectedLead.buyerProfile} />}
                        </div>
                    </div>

                    {/* Amenities */}
                    <div>
                        <h4 className="font-medium mb-2 text-my-black/60 ">Amenities</h4>
                        <p className="text-my-black/90 font-normal text-base">{selectedLead.amenities}</p>
                    </div>

                    {/* Requirements */}
                    <div>
                        <h4 className="font-medium mb-2 text-my-black/60 ">Other Specific Requirements</h4>
                        <p className="text-my-black/90 font-normal text-base">{selectedLead.otherSpecificRequirements}</p>
                    </div>
                    <Divider />

                    {/* Contact Information */}
                    <div className="bg-my-gray/10 p-4 rounded-lg mb-6">
                        <h2 className="text-xl font-semibold mb-6">Contact Information</h2>
                        <div className="space-y-1">
                            {selectedLead.contactInfo[0].name && (
                                <ContactItem Icon={PersonOutlineOutlined} label="Name" value={selectedLead.contactInfo[0].name} />
                            )}
                            {selectedLead.contactInfo[0].email && <ContactItem Icon={EmailOutlined} label="Email" value={selectedLead.contactInfo[0].email} />}
                            {selectedLead.contactInfo[0].phone && <ContactItem Icon={Phone} label="Phone Number" value={selectedLead.contactInfo[0].phone} />}
                            {selectedLead.contactInfo[0].website && <ContactItem Icon={Language} label="Website" value={selectedLead.contactInfo[0].website} />}
                            {selectedLead.contactInfo[0].facebook && (
                                <ContactItem Icon={Facebook} label="Facebook" value={selectedLead.contactInfo[0].facebook} />
                            )}
                            {selectedLead.contactInfo[0].instagram && (
                                <ContactItem Icon={Instagram} label="Instagram" value={selectedLead.contactInfo[0].instagram} />
                            )}
                            {selectedLead.contactInfo[0].linkedin && (
                                <ContactItem Icon={LinkedIn} label="LinkedIn" value={selectedLead.contactInfo[0].linkedin} />
                            )}
                            {selectedLead.contactInfo[0].twitter && <ContactItem Icon={X} label="X" value={selectedLead.contactInfo[0].twitter} />}
                        </div>
                    </div>
                    {/*  */}
                </div>
            </div>
        </div>
    );
}

function ContactItem({ Icon, label, value }: { Icon: React.ElementType<any, keyof React.JSX.IntrinsicElements>; label: string; value: string }) {
    const content = (
        <div className="flex default-sm:items-center gap-4 py-2 default-sm:flex-row flex-col font-poppins">
            <div className="flex items-center gap-3 w-[40%]">
                <Icon className="w-5 h-5 text-gray-600" />
                <span className="text-my-black/80 text-base default-sm:font-medium font-normal">{label}</span>
            </div>
            <span className="text-my-black/80 font-medium text-base w-[60%]">{value}</span>
        </div>
    );

    return content;
}

function Details({ title, value }: { title: string; value: string }) {
    return (
        <div className=" px-4 py-4 border border-my-gray/80 rounded-lg">
            <div className="text-my-black/50 text-sm mb-1">{title}</div>
            <div className="font-medium text-my-black/80">{value}</div>
        </div>
    );
}
